/* ============================================================
   PetNGo — Chrome JS (search overlay behaviour).

   OWNERSHIP: surface "chrome". Powers the bespoke header search
   overlay (petngo-search-overlay.liquid): icon trigger → top-drop
   panel with native Shopify search + a lightweight predictive
   dropdown. No external dependencies; never mutates shared state;
   safe to load on every template.

   Predictive results use Shopify's documented JSON endpoint
   (`routes.predictive_search_url` + `?q=&resources[type]=…`),
   so behaviour does not depend on Minimog's own search component.

   Config is read from data-* attributes on the root element so the
   merchant's schema settings (limit, types, "view all" copy, locale
   strings) flow through without hardcoding.
   ============================================================ */
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-pn-search-overlay]';
  var DEBOUNCE_MS = 220;
  var MIN_QUERY = 2;

  function escapeHtml(value) {
    if (value == null) return '';
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function debounce(fn, wait) {
    var t = null;
    return function () {
      var ctx = this;
      var args = arguments;
      if (t) clearTimeout(t);
      t = setTimeout(function () {
        t = null;
        fn.apply(ctx, args);
      }, wait);
    };
  }

  /* ----- Predictive search (returns a fresh DOM string) ----- */
  function buildSuggestMarkup(query, data, config) {
    var resources = (data && data.resources && data.resources.results) || {};
    var products = resources.products || [];
    var collections = resources.collections || [];
    var pages = resources.pages || [];
    var queries = resources.queries || [];

    if (
      !products.length &&
      !collections.length &&
      !pages.length &&
      !queries.length
    ) {
      return (
        '<p class="pn-search-suggest__empty">' +
        escapeHtml(config.emptyText) +
        '</p>'
      );
    }

    var html = '';

    if (queries.length) {
      html += '<div class="pn-search-suggest__group">';
      html +=
        '<h2 class="pn-search-suggest__heading">' +
        escapeHtml(config.suggestionsLabel) +
        '</h2>';
      queries.forEach(function (q) {
        html +=
          '<a class="pn-search-suggest__item" href="' +
          escapeHtml(q.url) +
          '"><span class="pn-search-suggest__title">' +
          escapeHtml(q.text) +
          '</span></a>';
      });
      html += '</div>';
    }

    if (products.length) {
      html += '<div class="pn-search-suggest__group">';
      html +=
        '<h2 class="pn-search-suggest__heading">' +
        escapeHtml(config.productsLabel) +
        '</h2>';
      products.forEach(function (p) {
        var rawUrl = p.featured_image && p.featured_image.url;
        var img;
        if (rawUrl) {
          // Pedir 104px al CDN (2x de 52) = thumb nítido en retina y descarga
          // ligera, en vez de servir el packshot original de 2000px.
          var sized =
            rawUrl + (rawUrl.indexOf('?') > -1 ? '&' : '?') + 'width=104';
          img =
            '<img class="pn-search-suggest__thumb" src="' +
            escapeHtml(sized) +
            '" alt="" width="52" height="52" loading="lazy">';
        } else {
          img = '<span class="pn-search-suggest__thumb"></span>';
        }
        var price = p.price ? formatMoney(p.price) : '';
        html +=
          '<a class="pn-search-suggest__item" href="' +
          escapeHtml(p.url) +
          '">' +
          img +
          '<span class="pn-search-suggest__title">' +
          escapeHtml(p.title) +
          '</span>' +
          (price
            ? '<span class="pn-search-suggest__price">' + price + '</span>'
            : '') +
          '</a>';
      });
      html += '</div>';
    }

    if (collections.length) {
      html += '<div class="pn-search-suggest__group">';
      html +=
        '<h2 class="pn-search-suggest__heading">' +
        escapeHtml(config.collectionsLabel) +
        '</h2>';
      collections.forEach(function (c) {
        html +=
          '<a class="pn-search-suggest__item" href="' +
          escapeHtml(c.url) +
          '"><span class="pn-search-suggest__title">' +
          escapeHtml(c.title) +
          '</span></a>';
      });
      html += '</div>';
    }

    if (pages.length) {
      html += '<div class="pn-search-suggest__group">';
      html +=
        '<h2 class="pn-search-suggest__heading">' +
        escapeHtml(config.pagesLabel) +
        '</h2>';
      pages.forEach(function (pg) {
        html +=
          '<a class="pn-search-suggest__item" href="' +
          escapeHtml(pg.url) +
          '"><span class="pn-search-suggest__title">' +
          escapeHtml(pg.title) +
          '</span></a>';
      });
      html += '</div>';
    }

    var allHref =
      config.searchUrl + '?q=' + encodeURIComponent(query);
    html +=
      '<a class="pn-search-suggest__all" href="' +
      escapeHtml(allHref) +
      '">' +
      escapeHtml(config.viewAllText).replace('{{ query }}', escapeHtml(query)) +
      '</a>';

    return html;
  }

  var productUrlStatusCache = {};

  function filterBrokenProductUrls(data) {
    var resources = (data && data.resources && data.resources.results) || {};
    var products = resources.products || [];
    if (!products.length || typeof fetch !== 'function') {
      return Promise.resolve(data);
    }

    var checks = products.map(function (product) {
      var url = product && product.url;
      if (!url) return Promise.resolve(false);

      var path = url.split('?')[0];
      if (Object.prototype.hasOwnProperty.call(productUrlStatusCache, path)) {
        return Promise.resolve(productUrlStatusCache[path]);
      }

      return fetch(path, {
        method: 'HEAD',
        credentials: 'same-origin',
      })
        .then(function (res) {
          var isRenderable = res.status !== 404 && res.status !== 410;
          productUrlStatusCache[path] = isRenderable;
          return isRenderable;
        })
        .catch(function () {
          productUrlStatusCache[path] = true;
          return true;
        });
    });

    return Promise.all(checks).then(function (flags) {
      resources.products = products.filter(function (_product, index) {
        return flags[index];
      });
      return data;
    });
  }

  /* La predictive search devuelve `price` como string decimal YA en la
     moneda de la tienda (ej. "110.00"), NO en centavos. Antes se dividía
     entre 100 → mostraba "1.10" en vez de "MX$110.00". Formateamos con
     prefijo MX$ y separadores de miles es-MX (paridad Hydrogen). */
  function formatMoney(value) {
    var amount = parseFloat(value);
    if (isNaN(amount)) return '';
    return (
      'MX$' +
      amount.toLocaleString('es-MX', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );
  }


  /* ===== Buscador propio: MiniSearch sobre el índice del theme (con sinónimos) =====
     Progressive enhancement: si el índice/lib fallan, cae a la predictive nativa. */
  function pnFold(s) {
    return (s == null ? '' : String(s)).normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  }
  function pnTokenize(text) {
    var out = [];
    pnFold(text).split(/\s+/).forEach(function (w) {
      if (!w) return;
      var parts = w.split(/[^a-z0-9]+/).filter(Boolean);
      for (var i = 0; i < parts.length; i++) out.push(parts[i]);
      if (parts.length > 1) out.push(parts.join(''));
    });
    return out;
  }
  // Sinónimos del rubro mascotas (folded, sin acentos). Resuelve que buscar
  // "flyharness" encuentre "Arnés"/"Pechera", etc.
  var SYN = {
    arnes: ['pechera', 'harness', 'flyharness'], pechera: ['arnes', 'harness', 'flyharness'],
    harness: ['arnes', 'pechera', 'flyharness'], flyharness: ['arnes', 'pechera', 'harness'], flyharnes: ['arnes', 'pechera', 'harness'],
    correa: ['leash', 'lead'], leash: ['correa'], lead: ['correa'],
    arena: ['arenero', 'litter', 'sanitaria'], arenero: ['arena', 'litter'], litter: ['arena', 'arenero'],
    croqueta: ['alimento', 'comida', 'pienso', 'croquetas'], croquetas: ['alimento', 'comida'], alimento: ['croqueta', 'comida', 'pienso'], comida: ['croqueta', 'alimento'], pienso: ['croqueta', 'alimento'],
    premio: ['premios', 'treat', 'snack', 'galleta'], premios: ['premio', 'treat', 'snack'], treat: ['premio', 'snack'], snack: ['premio', 'treat'],
    plato: ['comedero', 'bowl', 'tazon'], comedero: ['plato', 'bowl'], bowl: ['plato', 'comedero'], bebedero: ['bowl', 'plato'],
    juguete: ['toy', 'juguetes'], juguetes: ['juguete', 'toy'], toy: ['juguete'],
    cama: ['bed', 'colchon'], bed: ['cama'],
    shampoo: ['champu'], champu: ['shampoo'],
    cepillo: ['brush', 'deslanador'], brush: ['cepillo']
  };
  // Conceptos de CATEGORÍA: el cliente escribe una palabra que el catálogo
  // nombra distinto (ej. "medicina" -> el catálogo la clasifica como "Farmacia").
  // El valor es el término REAL del catálogo; pega en el productType (campo y).
  var CAT = {
    // Farmacia / salud
    medicina: 'farmacia', medicinas: 'farmacia', medicamento: 'farmacia', medicamentos: 'farmacia', medicinal: 'farmacia',
    farmacia: 'farmacia', remedio: 'farmacia', remedios: 'farmacia', tratamiento: 'farmacia', pastilla: 'farmacia', pastillas: 'farmacia', medicine: 'farmacia',
    desparasitante: 'farmacia', desparasitantes: 'farmacia', desparasitar: 'farmacia', antiparasitario: 'farmacia', lombrices: 'farmacia',
    antipulgas: 'farmacia', pulga: 'farmacia', pulgas: 'farmacia', garrapata: 'farmacia', garrapatas: 'farmacia', pipeta: 'farmacia', pipetas: 'farmacia', antibiotico: 'farmacia',
    // Suplementos
    vitamina: 'suplementos', vitaminas: 'suplementos', suplemento: 'suplementos', suplementos: 'suplementos', omega: 'suplementos', probiotico: 'suplementos',
    // Juguetes (incluye la categoría "Pelotas y Peluches", que no dice "juguete")
    juguete: 'juguete pelota peluche', juguetes: 'juguete pelota peluche', toy: 'juguete pelota peluche',
    pelota: 'pelota juguete', pelotas: 'pelota juguete', peluche: 'peluche juguete', peluches: 'peluche juguete', mordedera: 'juguete morder',
    // Camas
    cama: 'camas casas', camas: 'camas casas', colchon: 'camas', casa: 'casas camas',
    // Estética / higiene
    estetica: 'estetica higiene', higiene: 'estetica higiene',
    // Limpieza / olores
    limpieza: 'limpieza olores', olor: 'limpieza olores', olores: 'limpieza olores',
    // Platos / comederos
    comedero: 'platos alimentadores', bebedero: 'platos alimentadores', tazon: 'platos alimentadores', alimentador: 'platos alimentadores',
    // Collares
    collar: 'collares', collares: 'collares',
    // Golosinas / premios
    golosina: 'premios golosinas', golosinas: 'premios golosinas',
    // Ropa
    ropa: 'ropa', sueter: 'ropa', playera: 'ropa', abrigo: 'ropa', impermeable: 'ropa',
    // Transportadoras
    transportadora: 'transportadoras', transportadoras: 'transportadoras', jaula: 'transportadoras', kennel: 'transportadoras',
    // Rascadores
    rascador: 'rascadores', rascadores: 'rascadores',
    // Bolsas para desechos / sanitarias (el cliente escribe "bolsa"; el término
    // "bolsas" también aparece en premios — expandimos a las señales de bolsa de
    // desechos para que esas ganen y no salgan premios/cepillos)
    bolsa: 'bolsas sanitarias desechos heces biodegradables poop', bolsas: 'bolsas sanitarias desechos heces biodegradables poop',
    poop: 'bolsas sanitarias desechos heces biodegradables poop', heces: 'bolsas sanitarias desechos heces biodegradables poop',
    popo: 'bolsas sanitarias desechos heces biodegradables poop', excremento: 'bolsas sanitarias desechos heces biodegradables poop'
  };
  // Boost para búsqueda por CATEGORÍA: prioriza el productType (y) y tags (g),
  // baja el título, para que "medicina" traiga la categoría Farmacia por TIPO,
  // no por coincidencias sueltas en el nombre.
  var CATBOOST = { y: 6, g: 4, t: 2, v: 1, s: 1, b: 1 };
  // Palabras vacías (no aportan a la categoría): "medicina PARA gato".
  var STOP = { para: 1, de: 1, el: 1, la: 1, los: 1, las: 1, con: 1, y: 1, un: 1, una: 1, mi: 1, tu: 1, del: 1, al: 1, en: 1, o: 1 };
  var BOOST = { t: 4, g: 3, y: 3, v: 3, s: 2, b: 1 };
  var _localPromise = null;
  function pnLoadScript(src) {
    return new Promise(function (res, rej) {
      if (window.MiniSearch) return res();
      var s = document.createElement('script');
      s.src = src; s.async = true;
      s.onload = function () { res(); };
      s.onerror = function () { rej(new Error('minisearch load failed')); };
      document.head.appendChild(s);
    });
  }
  function getLocalSearch() {
    if (_localPromise) return _localPromise;
    var el = document.querySelector('[data-search-index]');
    var idxUrl = el && el.getAttribute('data-search-index');
    var libUrl = el && el.getAttribute('data-minisearch-src');
    if (!idxUrl || !libUrl || typeof fetch !== 'function' || typeof Promise === 'undefined') {
      _localPromise = Promise.reject(new Error('local search unavailable'));
      return _localPromise;
    }
    _localPromise = pnLoadScript(libUrl)
      .then(function () {
        return fetch(idxUrl, { credentials: 'same-origin' }).then(function (r) {
          if (!r.ok) throw new Error('index ' + r.status);
          return r.json();
        });
      })
      .then(function (docs) {
        var mini = new window.MiniSearch({
          idField: 'id',
          fields: ['t', 'v', 'y', 'g', 's', 'b'],
          storeFields: ['t', 'v', 'p', 'img', 'u', 'w', 'x', 'r'],
          tokenize: function (t) { return pnTokenize(t); },
          processTerm: function (t) { return pnFold(t); }
        });
        mini.addAll(docs);
        return mini;
      });
    return _localPromise;
  }
  function _mergeUnique(a, b) {
    var seen = {}, out = [];
    a.concat(b).forEach(function (r) { if (!seen[r.id]) { seen[r.id] = 1; out.push(r); } });
    return out;
  }
  // AND directo → OR si pocos → sinónimos si aún pocos. Devuelve la forma que
  // espera buildSuggestMarkup (products con title/url/featured_image/price).
  function localQuery(mini, query, limit) {
    limit = limit || 8;
    var folded = pnFold(query);
    var toks = folded.split(/[^a-z0-9]+/).filter(Boolean);
    var mapFn = function (r) {
      return { title: r.t, vendor: r.v, url: r.u, price: r.p, featured_image: r.img ? { url: r.img } : null, options: r.w || null, options_avail: r.x || null, price_range: r.r === 1 };
    };
    // Concepto(s) de CATEGORÍA en la consulta (cliente escribe "medicina",
    // el catálogo lo nombra "Farmacia"). Traducimos al término real del catálogo.
    var cats = [], nonCat = [];
    toks.forEach(function (t) {
      var c = CAT[t] || CAT[t.replace(/s$/, '')];
      if (c) { if (cats.indexOf(c) < 0) cats.push(c); }
      else if (!STOP[t]) nonCat.push(t);
    });
    // (A) Si la consulta trae categoría, búscala por su nombre real (pega en el
    //     productType, boost alto) con precisión — sin el fuzzy que mete ruido.
    //     Los tokens no-categoría (marca, especie) van también → afinan especie.
    if (cats.length) {
      var cr = mini.search(cats.concat(nonCat).join(' '), { boost: CATBOOST, prefix: true, combineWith: 'OR' });
      if (cr.length >= 3) return cr.slice(0, limit).map(mapFn);
    }
    // (B) Flujo normal: AND -> sinónimos -> OR difuso (último recurso).
    var res = mini.search(folded, { boost: BOOST, prefix: true, fuzzy: 0.15, combineWith: 'AND' });
    if (res.length < 3) {
      var exp = [];
      toks.forEach(function (t) { exp.push(t); (SYN[t] || []).forEach(function (s) { exp.push(pnFold(s)); }); });
      if (exp.length > toks.length) {
        res = _mergeUnique(res, mini.search(exp.join(' '), { boost: BOOST, prefix: true, fuzzy: 0.15, combineWith: 'OR' }));
      }
    }
    if (res.length < 3) {
      res = _mergeUnique(res, mini.search(folded, { boost: BOOST, prefix: true, fuzzy: 0.2, combineWith: 'OR' }));
    }
    return res.slice(0, limit).map(mapFn);
  }
  // Página /search: pinta la grilla client-side (oculta la nativa).
  function initSearchPage() {
    var page = document.querySelector('[data-pn-search-page]');
    if (!page) return;
    var q = '';
    try { q = (new URLSearchParams(window.location.search).get('q') || '').trim(); } catch (e) { q = ''; }
    var box = page.querySelector('[data-pn-search-results]');
    var nativeGrid = page.querySelector('[data-pn-native-results]');
    var countEl = page.querySelector('[data-pn-search-count]');
    if (!box || !q) return;
    box.hidden = false;
    box.innerHTML = '<div class="pn-ms-loading">Buscando…</div>';
    getLocalSearch().then(function (mini) {
      var products = localQuery(mini, q, 60);
      if (nativeGrid) nativeGrid.style.display = 'none';
      if (!products.length) {
        box.innerHTML = '<div class="pn-ms-empty">No encontramos productos para "' + escapeHtml(q) + '".</div>';
        if (countEl) countEl.textContent = '0 productos';
        return;
      }
      var html = '<div class="pn-ms-grid">';
      products.forEach(function (p) {
        var iu = p.featured_image && p.featured_image.url;
        var img = iu
          ? '<img class="pn-ms-card__img" src="' + escapeHtml(iu + (iu.indexOf('?') > -1 ? '&' : '?') + 'width=360') + '" alt="" loading="lazy">'
          : '<span class="pn-ms-card__img"></span>';
        // Chips de variante (paridad con la card del PLP): hasta 4 + "+N",
        // atenuadas cuando la variante está agotada.
        var opts = '';
        if (p.options && p.options.length > 1) {
          var MAXCHIPS = 4;
          opts = '<span class="pn-ms-card__opts">';
          p.options.slice(0, MAXCHIPS).forEach(function (o, i) {
            var oos = p.options_avail && p.options_avail[i] === 0 ? ' pn-ms-chip--oos' : '';
            opts += '<span class="pn-ms-chip' + oos + '">' + escapeHtml(o) + '</span>';
          });
          if (p.options.length > MAXCHIPS) {
            opts += '<span class="pn-ms-chip pn-ms-chip--more">+' + (p.options.length - MAXCHIPS) + '</span>';
          }
          opts += '</span>';
        }
        var desde = p.price_range ? '<span class="pn-ms-card__from">Desde </span>' : '';
        var price = p.price ? '<span class="pn-ms-card__price">' + desde + formatMoney(p.price) + '</span>' : '';
        html += '<a class="pn-ms-card" href="' + escapeHtml(p.url) + '">' + img +
          '<span class="pn-ms-card__vendor">' + escapeHtml(p.vendor || '') + '</span>' +
          '<span class="pn-ms-card__title">' + escapeHtml(p.title) + '</span>' + opts + price + '</a>';
      });
      html += '</div>';
      box.innerHTML = html;
      if (countEl) countEl.textContent = products.length + ' productos';
    }).catch(function () {
      box.hidden = true;
      if (nativeGrid) nativeGrid.style.display = '';
    });
  }

  function PnSearchOverlay(root) {
    var doc = document;
    var config = {
      predictiveUrl: root.getAttribute('data-predictive-url') || '',
      searchUrl: root.getAttribute('data-search-url') || '/search',
      limit: root.getAttribute('data-limit') || '6',
      types: root.getAttribute('data-types') || 'product',
      sectionId: root.getAttribute('data-section-id') || '',
      emptyText: root.getAttribute('data-empty-text') || 'Sin resultados.',
      viewAllText:
        root.getAttribute('data-view-all-text') ||
        'Ver todos los resultados',
      suggestionsLabel:
        root.getAttribute('data-label-suggestions') || 'Sugerencias',
      productsLabel:
        root.getAttribute('data-label-products') || 'Productos',
      collectionsLabel:
        root.getAttribute('data-label-collections') || 'Colecciones',
      pagesLabel: root.getAttribute('data-label-pages') || 'Páginas',
    };

    var trigger = root.querySelector('[data-pn-search-trigger]');
    var overlay = root.querySelector('[data-pn-search-overlay-panel]');
    var closeBtn = root.querySelector('[data-pn-search-close]');
    var input = root.querySelector('[data-pn-search-input]');
    var form = root.querySelector('[data-pn-search-form]');
    var suggest = root.querySelector('[data-pn-search-suggest]');

    // Defensive: if the section markup is incomplete, do nothing
    // rather than throwing on every page load.
    if (!trigger || !overlay || !input) return;

    var isOpen = false;
    var prevOverflow = '';
    var controller = null;

    function open() {
      if (isOpen) return;
      isOpen = true;
      overlay.classList.add('is-open');
      overlay.setAttribute('aria-hidden', 'false');
      trigger.setAttribute('aria-expanded', 'true');
      prevOverflow = doc.body.style.overflow;
      doc.body.style.overflow = 'hidden';
      // Defer focus so the slide-down animation can start first.
      window.setTimeout(function () {
        try {
          input.focus();
        } catch (e) {
          /* no-op */
        }
      }, 90);
    }

    function close() {
      if (!isOpen) return;
      isOpen = false;
      overlay.classList.remove('is-open');
      overlay.setAttribute('aria-hidden', 'true');
      trigger.setAttribute('aria-expanded', 'false');
      doc.body.style.overflow = prevOverflow;
      if (controller) {
        controller.abort();
        controller = null;
      }
      // Restore focus to the trigger for keyboard continuity.
      window.setTimeout(function () {
        try {
          trigger.focus();
        } catch (e) {
          /* no-op */
        }
      }, 80);
    }

    function runPredictive(query) {
      if (!suggest) return;
      if (form) form.classList.add('is-loading');
      getLocalSearch().then(function (mini) {
        if (!isOpen) return true;
        var products = localQuery(mini, query, parseInt(config.limit, 10) || 6);
        if (products && products.length) {
          suggest.innerHTML = buildSuggestMarkup(query, { resources: { results: { products: products } } }, config);
          if (form) form.classList.remove('is-loading');
          return true;
        }
        return false;
      }).then(function (handled) {
        if (handled || !isOpen) return;
        runNativePredictive(query);
      }).catch(function () {
        runNativePredictive(query);
      });
    }

    function runNativePredictive(query) {
      if (!config.predictiveUrl || !suggest) return;
      if (controller) controller.abort();
      controller =
        typeof AbortController !== 'undefined'
          ? new AbortController()
          : null;

      var params =
        '?q=' +
        encodeURIComponent(query) +
        '&resources[limit]=' +
        encodeURIComponent(config.limit) +
        '&resources[options][unavailable_products]=last';
      // Shopify's predictive search espera UN solo `resources[type]` con los
      // tipos separados por coma. Si se mandan params repetidos
      // (`resources[type]=product&resources[type]=collection`) Shopify solo
      // honra el ÚLTIMO y descarta el resto → el dropdown salía vacío. Unir
      // en un solo valor coma-separado devuelve product+collection+query.
      var typeList = config.types
        .split(',')
        .map(function (t) { return t.trim(); })
        .filter(Boolean);
      if (typeList.length) {
        params += '&resources[type]=' + encodeURIComponent(typeList.join(','));
      }
      if (config.sectionId) {
        params += '&section_id=' + encodeURIComponent(config.sectionId);
      }

      if (form) form.classList.add('is-loading');

      fetch(config.predictiveUrl + params, {
        headers: { Accept: 'application/json' },
        signal: controller ? controller.signal : undefined,
      })
        .then(function (res) {
          if (!res.ok) throw new Error('predictive_search ' + res.status);
          return res.json();
        })
        .then(filterBrokenProductUrls)
        .then(function (data) {
          if (!isOpen) return;
          suggest.innerHTML = buildSuggestMarkup(query, data, config);
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          // Predictive is progressive enhancement — on failure we
          // simply clear the dropdown; the form still submits to the
          // native results page.
          if (suggest) suggest.innerHTML = '';
        })
        .then(function () {
          if (form) form.classList.remove('is-loading');
        });
    }

    var onInput = debounce(function () {
      var query = input.value.trim();
      if (query.length < MIN_QUERY) {
        if (suggest) suggest.innerHTML = '';
        if (form) form.classList.remove('is-loading');
        if (controller) {
          controller.abort();
          controller = null;
        }
        return;
      }
      runPredictive(query);
    }, DEBOUNCE_MS);

    trigger.addEventListener('click', open);
    if (closeBtn) closeBtn.addEventListener('click', close);

    // Backdrop click (only when the click lands on the scrim itself).
    overlay.addEventListener('click', function (event) {
      if (event.target === overlay) close();
    });

    input.addEventListener('input', onInput);

    // Navegación con teclado dentro del dropdown: ↑/↓ recorren los
    // resultados, Enter abre el activo (sin él, el form busca normal).
    input.addEventListener('keydown', function (event) {
      if (!suggest) return;
      var items = suggest.querySelectorAll(
        '.pn-search-suggest__item, .pn-search-suggest__all'
      );
      if (!items.length) return;
      var current = suggest.querySelector('.is-active');
      var idx = current
        ? Array.prototype.indexOf.call(items, current)
        : -1;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        idx = (idx + 1) % items.length;
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        idx = (idx - 1 + items.length) % items.length;
      } else if (event.key === 'Enter') {
        if (current) {
          event.preventDefault();
          window.location.href = current.getAttribute('href');
        }
        return;
      } else {
        return;
      }
      if (current) current.classList.remove('is-active');
      var next = items[idx];
      next.classList.add('is-active');
      next.scrollIntoView({ block: 'nearest' });
    });

    doc.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && isOpen) close();
    });
  }

  function init() {
    try { initSearchPage(); } catch (e) {}
    var roots = document.querySelectorAll(ROOT_SELECTOR);
    Array.prototype.forEach.call(roots, function (root) {
      if (root.getAttribute('data-pn-initialized') === 'true') return;
      root.setAttribute('data-pn-initialized', 'true');
      try {
        new PnSearchOverlay(root);
      } catch (e) {
        if (window.console && console.warn) {
          console.warn('[petngo-chrome] search overlay init failed', e);
        }
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init when the Theme Editor re-renders the section.
  document.addEventListener('shopify:section:load', init);
})();

